import {defineStore} from 'pinia'
import axios from 'axios'
import Logger from '../utils/logger'
import {applyMarketingRounding, stagePrice} from '../utils/pricing'

const logger = new Logger('configurateur')

export const useConfigurateurStore = defineStore('configurateur', {
    state: () => ({
        // Configuration principale
        from: 'voiture',
        step: null,

        // Type d'expérience
        experience: 'pilotage', // 'pilotage' ou 'bapteme'

        // Identifiants principaux
        voiture_id: null,
        circuit_id: null,
        stage_id: null,

        // Données des tours
        nb_tours: 0,
        nb_tours_bapteme: 0,
        possibilite: null,

        // Données des étapes complémentaires
        stage_ids: [],
        options: [],

        // Collections de données
        stages: [],
        circuits: [],
        voitures: [],
        circuitsVoitures: [],
        autreStages: [],
        ktmStage: null,

        // Images configurables des cartes de l'étape date (null = fallback sur l'image par défaut bundlée)
        configurateurImages: {
            cadeau: null,
            date: null,
        },

        // Gestion des dates et créneaux
        circuit_date_creneau_id: null,
        circuit_date_creneau_voiture_id: null,
        circuit_date_id: null,
        creneau_human: null,
        dateMode: 'cadeau',
        date: null,
        creneau: null,
        currentMonth: null,
        currentDate: null,
        stepDate: 'month',

        // Date pré-sélectionnée (YYYY-MM-DD) provenant du datepicker de la fiche produit
        preselectedDateIso: null,

        // États UI
        modal: null,
        modalLoader: false,
        isLoadingStep: false,
        stepRequestVersion: 0,
        loaderPlanningFront: false,
        prevGalleryMode: null,

        // Flags et debug
        is_fetch: {
            tour: false,
        },
        debug_price: true,
    }),

    persist: {
        // storage: localStorage, // Optionnel: localStorage est le défaut
        pick: ['stage_id', 'voiture_id', 'circuit_id', 'nb_tours'], // Spécifie les clés à persister
    },

    getters: {
        // Getters de base pour les entités principales
        voiture: (state) => {
            if (!state.voiture_id || !state.voitures.length) {
                return {};
            }
            return state.voitures.find(voiture => voiture.id == state.voiture_id);
        },
        circuit: (state) => {
            if (!state.circuit_id || !state.circuits.length) {
                return {};
            }
            return state.circuits.find(circuit => circuit.id == state.circuit_id);
        },
        autreStagesFiltered: (state) => {
            if (!state.autreStages.length) {
                return [];
            }

            logger.log('🔍 autreStagesFiltered - Début du filtrage');
            logger.log('🔍 autreStages.length:', state.autreStages.length);

            // Get the main stage for subcategory filtering
            const mainStage = (state.experience === 'bapteme' && state.nb_tours_bapteme > 0)
                ? state.stageBapteme
                : state.stage;

            logger.log('🔍 Main stage pour filtrage:', {
                stage_id: mainStage?.stage_id,
                sous_categorie_id: mainStage?.sous_categorie_id,
                experience: state.experience
            });

            let filtered = [...state.autreStages]; // Clone to avoid mutations

            // RÈGLE PRINCIPALE : Filter by subcategory
            if (mainStage && mainStage.sous_categorie_id !== null && mainStage.sous_categorie_id !== undefined) {
                logger.log('🔍 Stage principal a sous_categorie_id:', mainStage.sous_categorie_id);
                logger.log('🔍 Avant filtrage sous-catégorie, nombre de stages:', filtered.length);

                filtered = filtered.filter(stage => {
                    const stageSubcategoryId = stage.sous_categorie_id;
                    const shouldInclude = stageSubcategoryId === mainStage.sous_categorie_id;

                    logger.log('🔍 Stage:', {
                        voiture_title: stage.voiture_title,
                        sous_categorie_id: stageSubcategoryId,
                        shouldInclude: shouldInclude
                    });

                    return shouldInclude;
                });

                logger.log('🔍 Après filtrage sous-catégorie, nombre de stages:', filtered.length);
            } else {
                logger.log('🔍 Stage principal sans sous_categorie_id, pas de filtrage par sous-catégorie');
            }

            // Apply existing date/creneau filtering
            if (!state.circuit_date_id && !state.circuit_date_creneau_id) {
                logger.log('🔍 Pas de filtrage par date/créneau');
                return filtered;
            }

            logger.log('🔍 Application du filtrage par date/créneau');
            const finalFiltered = filtered.filter(stage => {
                const date = stage.dates?.[state.circuit_date_id];
                if (state.circuit_date_creneau_id) {
                    return date?.creneaux?.some(creneau => creneau.circuit_date_creneau_id == state.circuit_date_creneau_id);
                }
                return Boolean(date);
            });

            logger.log('🔍 Résultat final du filtrage:', finalFiltered.length, 'stages');
            return finalFiltered;
        },
        stage: (state) => {
            logger.log('🐛 Store stage getter appelé:', {
                stages_length: state.stages.length,
                stages: state.stages.map(s => ({
                    stage_id: s.stage_id,
                    product_id: s.product_id,
                    is_bapteme: s.is_bapteme,
                    has_possibilites: !!s.possibilites,
                    possibilites_length: s.possibilites?.length
                })),
                voiture_id: state.voiture_id,
                circuit_id: state.circuit_id
            });

            if (!state.stages.length) {
                logger.log('🚨 Pas de stages disponibles - retour objet vide');
                return {};
            }
            if (state.stages.length == 1) {
                logger.log('🔍 Un seul stage trouvé:', state.stages[0].stage_id);
                return state.stages[0];
            }
            const foundStage = state.stages.find(v => !v.is_bapteme);
            logger.log('🔍 Stage trouvé avec !is_bapteme:', foundStage ? foundStage.stage_id : 'AUCUN TROUVE');
            return foundStage || {};
        },
        stageBapteme: (state) => {
            if (!state.stages.length) {
                return {};
            }
            return state.stages.find(v => v.is_bapteme) || {};
        },
        stageBaptemeInStageIds: (state) => {
            if (!state.stageBapteme || !state.stage_ids.length) {
                return null
            }
            return state.stage_ids.find(v => v.stage_id == (state.stageBapteme.stage_id ?? state.stageBapteme.id))
        },
        voitureCircuit: (state) => {
            if (!state.voiture || !state.voiture.circuits || !state.circuit_id) {
                return null;
            }
            return state.voiture.circuits.find(circuit => circuit.circuit_id == state.circuit_id);
        },
        circuitValidForVoiture: (state) => {
            return (circuitId) => state.voiture && state.voiture.circuit_ids && state.voiture.circuit_ids.find(v => v == circuitId) !== undefined;
        },
        voitureValidForCircuit: (state) => {
            return (voitureId) => state.circuit && state.circuit.voiture_ids && state.circuit.voiture_ids.find(v => v == voitureId) !== undefined;
        },

        // Getters pour les tours
        rangeTours: (state) => {
            if (!state.stage) {
                return [];
            }
            return state.stage.tours_conseilles;
        },
        baptemeRangeTours: (state) => {
            if (!state.stageBapteme) {
                return [];
            }
            // Vérifie si tours_conseilles est un tableau non vide
            if (Array.isArray(state.stageBapteme.tours_conseilles) && state.stageBapteme.tours_conseilles.length > 0) {
                return state.stageBapteme.tours_conseilles;
            }
            // Sinon, retourne tours
            return state.stageBapteme.tours || [];
        },
        nbVoitures: (state) => {
            if (!state.stage) {
                return 0;
            }
            return 1 + state.stage_ids.length;
        },

        // Getters pour les options
        optionRecoFree: (state) => {
            if (!state.circuit) {
                return false
            }
            return state.circuit.option_reco
        },

        // Getters pour les étapes
        steps: (state) => {
            logger.log('🔍 Getter steps appelé - experience actuelle:', state.experience);
            logger.log('🔍 FROM :', state.from);
            let steps = []
            if (state.from == 'circuit') {
                steps.push('circuit')
                steps.push('voiture')
            } else {
                steps.push('voiture')
                steps.push('circuit')
            }
            steps.push('tour')
            steps.push('creneau')
            steps.push('option')

            // Step 6 complement is now available for both baptême and pilotage
            // with independent mode selection per car
            steps.push('complement')

            logger.log('🔍 Steps finales:', steps);
            return steps;
        },
        first_step: (state) => {
            return state.steps[0];
        },
        last_step: (state) => {
            return state.steps[state.steps.length - 1];
        },
        isLastStep: (state) => state.step === state.steps[state.steps.length - 1],
        next_step: (state) => {
            let index = state.steps.indexOf(state.step);
            if (index === -1) {
                return null;
            }
            if (index === state.steps.length - 1) {
                return null;
            }
            return state.steps[index + 1];
        },
        prev_step: (state) => {
            let index = state.steps.indexOf(state.step);
            if (index === -1) {
                return null;
            }
            if (index === 0) {
                return null;
            }
            return state.steps[index - 1];
        },

        // Getters pour l'affichage et les médias
        displayVideo: (state) => {
            return (state.step == 'circuit' || state.stepCircuitVoitureCompleted) && state.hasVoitureCircuitVideo
        },
        stepCircuitVoitureCompleted: (state) => {
            if (['circuit', 'voiture'].includes(state.step)) {
                return false;
            }
            return true;
        },
        hasVoitureCircuitVideo: (state) => {
            if (state.voitureCircuit && state.voitureCircuit.video) {
                return true;
            }
            return false;
        },
        images: (state) => {
            let images = [];
            let galleryMode = null;
            if (state.voiture_id && state.step === 'voiture') {
                galleryMode = 'voiture';
            } else if (!state.voiture_id && state.step === 'voiture') {
                galleryMode = 'circuit';
            }
            if (state.circuit_id && state.step === 'circuit') {
                galleryMode = 'circuit';
            } else if (!state.circuit_id && state.step === 'circuit') {
                galleryMode = 'voiture';
            }

            if (!galleryMode && state.voitureCircuit && (state.voitureCircuit.image || []).length) {
                galleryMode = 'voitureCircuit';
            } else if (!galleryMode) {
                if (state.prevGalleryMode) {
                    galleryMode = state.prevGalleryMode;
                } else if ((state.voiture.images || []).length) {
                    galleryMode = 'voiture';
                } else if ((state.circuit.images || []).length) {
                    galleryMode = 'circuit';
                }
            }

            state.setPrevGalleryMode(galleryMode);
            switch (galleryMode) {
                case 'voiture':
                    images = [...(state.voiture.images || [])];
                    // Trier les images par collection_name pour les voitures
                    images = images.sort((a, b) => {
                        const order = {'principale': 1, 'profil': 2, 'fond': 3};
                        const orderA = order[a.collection_name] || 999;
                        const orderB = order[b.collection_name] || 999;
                        return orderA - orderB;
                    });
                    break;
                case 'circuit':
                    images = [...(state.circuit?.images || [])];
                    if (state.voitureCircuit && state.voitureCircuit.image) {
                        images.push({
                            original_url: state.voitureCircuit.image,
                            collection_name: 'voiture_circuit'
                        })
                    }
                    // Trier les images par collection_name pour les circuits.
                    // La collection 'circuit' contient le rendu 3D du tracé : la vraie
                    // photo ('principale') doit passer avant, le rendu 3D en second — #136
                    images = images.sort((a, b) => {
                        const order = {
                            'principale': 1,
                            'circuit': 2,
                            'voiture_circuit': 3,
                            'gallerie': 4,
                            'fond': 5,
                            'logo': 6,
                            'pilote': 7
                        };
                        const orderA = order[a.collection_name] || 999;
                        const orderB = order[b.collection_name] || 999;
                        return orderA - orderB;
                    });
                    break
                case "voitureCircuit":
                    images = [{
                        original_url: state.voitureCircuit.image
                    }];
                    break
            }
            return images;
        },

        // Getters pour les prix
        priceOptions: (state) => {
            return state.options.reduce((acc, option) => {
                return acc + (option.montant_ttc * option.quantity);
            }, 0)
        },
        // Prix dynamique d'une possibilité selon la date/créneau sélectionné
        getPossibilitePrice: (state) => {
            return (possibilite) => {
                if (!possibilite) return null;

                // Logique identique au getter 'price' principal
                let price = possibilite.price.normal;

                if (state.circuit_date_creneau_id && possibilite.price.creneau) {
                    price = possibilite.price.creneau;
                } else if (state.circuit_date_id && possibilite.price.date) {
                    price = possibilite.price.date;
                }

                return price;
            }
        },
        // Prix dynamique du baptême selon la date/créneau sélectionné
        baptemePriceDynamic: (state) => {
            if (!state.stageBapteme || !state.stageBapteme.prices) {
                return null;
            }

            let price = state.stageBapteme.prices.normal;

            // Look for slot-specific pricing first (most specific)
            if (state.circuit_date_creneau_id && state.stageBapteme.dates && state.circuit_date_id) {
                const dateData = state.stageBapteme.dates[state.circuit_date_id];
                if (dateData && dateData.creneaux) {
                    const creneauData = dateData.creneaux.find(c => c.circuit_date_creneau_id == state.circuit_date_creneau_id);
                    if (creneauData && creneauData.price) {
                        price = creneauData.price;
                    }
                }
            }
            // Then look for date-specific pricing
            else if (state.circuit_date_id && state.stageBapteme.dates) {
                const dateData = state.stageBapteme.dates[state.circuit_date_id];
                if (dateData && dateData.price) {
                    price = dateData.price;
                }
            }

            return price;
        },
        // Prix dynamique pour les tours conseillés du baptême
        baptemeRangeToursPrice: (state) => {
            const basePrice = state.baptemePriceDynamic;
            if (!basePrice || !state.stageBapteme || !state.stageBapteme.tours_conseilles) {
                return [];
            }

            return state.stageBapteme.tours_conseilles.map((tour, index) => {
                return {
                    ...tour,
                    price: stagePrice(basePrice, tour.nb_tours),
                    tour_number: tour.nb_tours
                };
            });
        },
        // Prix dynamique des stages complémentaires selon la date/créneau sélectionné
        getComplementStagePrice: (state) => {
            return (stage) => {
                if (!stage || !stage.prices) {
                    return null;
                }

                // Pour les stages dual-mode, récupérer le bon sous-stage selon l'onglet actuel
                let targetStage = stage;
                if (stage.dual_mode && stage.stages) {
                    // Déterminer le mode actuel (cette logique peut être adaptée selon l'UI)
                    // Pour l'instant, utiliser pilotage par défaut ou détecter selon stage.form.is_bapteme
                    const currentMode = stage.form && stage.form.is_bapteme ? 'bapteme' : 'pilotage';
                    if (stage.stages[currentMode]) {
                        targetStage = stage.stages[currentMode];
                    }
                }

                // Commencer avec le prix normal
                let price = targetStage.prices.normal;

                // Vérifier s'il y a une sélection de date/créneau
                if (state.circuit_date_creneau_id && state.circuit_date_id && targetStage.dates) {
                    const dateData = targetStage.dates[state.circuit_date_id];
                    if (dateData && dateData.creneaux) {
                        const creneauData = dateData.creneaux.find(c => c.circuit_date_creneau_id == state.circuit_date_creneau_id);
                        if (creneauData && creneauData.price) {
                            price = creneauData.price;
                        }
                    }
                } else if (state.circuit_date_id && targetStage.dates) {
                    const dateData = targetStage.dates[state.circuit_date_id];
                    if (dateData && dateData.price) {
                        price = dateData.price;
                    }
                }

                return price;
            }
        },
        priceAutreStages: (state) => {
            return state.stage_ids.reduce((acc, stageForm) => {
                // Use the stage form's price data directly since it contains mode-specific pricing
                const nbTour = stageForm.nb_tour || stageForm.nb_tour_min;
                return acc + stagePrice(stageForm, nbTour);
            }, 0)
        },
        // Ancien prix des stages complémentaires (étape 6)
        oldPriceAutreStages: (state) => {
            return state.stage_ids.reduce((acc, stageForm) => {
                const nbTour = stageForm.nb_tour || stageForm.nb_tour_min;
                const oldMontantTtc = stageForm.old_montant_ttc;
                const oldMontantTtcParTour = stageForm.old_montant_ttc_par_tour || stageForm.montant_ttc_par_tour;
                const nbTourMin = stageForm.nb_tour_min;

                let oldStagePrice = 0;
                if (oldMontantTtc) {
                    if (nbTour <= nbTourMin) {
                        oldStagePrice = oldMontantTtc;
                    } else {
                        oldStagePrice = oldMontantTtc + ((nbTour - nbTourMin) * oldMontantTtcParTour);
                    }
                }

                return acc + oldStagePrice;
            }, 0)
        },
        price: (state) => {
            // En mode baptême, utiliser stageBapteme ; sinon utiliser stage
            const mainStage = (state.experience === 'bapteme' && state.nb_tours_bapteme > 0)
                ? state.stageBapteme
                : state.stage;
            const mainNbTours = (state.experience === 'bapteme' && state.nb_tours_bapteme > 0)
                ? state.nb_tours_bapteme
                : state.nb_tours;

            logger.log('🔍 Calcul du prix pour le stage:', mainStage, 'en mode', state.experience);

            // Vérification de l'existence du stage principal et de sa structure
            if (!mainStage) {
                return {'total': 0, 'perTours': null};
            }

            let price;
            let subprice = 0;
            let perTours = 0;
            let pricePossibilite = 'non';
            let typePrice = 'base price';
            let priceFrom = 'unknown';

            // Distinguer entre stages avec possibilités et stages avec nombre de tours
            if (!mainStage.possibilites || !mainStage.possibilites.length) {
                // Stage avec nombre de tours - vérifier que prices.normal existe
                if (!mainStage.prices || !mainStage.prices.normal) {
                    logger.warn('🚨 Stage sans structure prices.normal:', mainStage);
                    return {'total': 0, 'perTours': null};
                }

                price = mainStage.prices.normal;
                priceFrom = 'normal, promo : ' + price.promo_type + ' - ' + price.promo;

                if (state.circuit_date_creneau_id) {
                    let found = mainStage.dates[state.circuit_date_id]?.creneaux.find(v => v.circuit_date_creneau_id == state.circuit_date_creneau_id);
                    if (found) {
                        // #137 : l'override créneau (TourStep) ne porte pas old_montant_ttc_par_tour
                        // ni nb_tour_min ; on les hérite de prices.normal pour que le prix barré
                        // reste cohérent quel que soit le nb de tours (sinon il "yoyote").
                        price = {...mainStage.prices.normal, ...found.price}
                        typePrice = 'creneau, promo : ' + price.promo_type + ' - ' + price.promo
                    }
                } else if (state.circuit_date_id) {
                    let found = mainStage.dates[state.circuit_date_id];
                    if (found) {
                        // #137 : idem créneau — on hérite old_montant_ttc_par_tour / nb_tour_min
                        // de prices.normal pour un prix barré cohérent selon le nb de tours.
                        price = {...mainStage.prices.normal, ...found.price}
                        typePrice = 'date, promo : ' + price.promo_type + ' - ' + price.promo
                    }
                }


                subprice = stagePrice(price, mainNbTours);
                perTours = null;
            } else {
                // Stage avec possibilités
                if (!state.possibilite || !state.possibilite.price) {
                    logger.warn('🚨 Possibilite non définie ou sans price:', state.possibilite);
                    return {'total': 0, 'perTours': null};
                }

                price = state.possibilite.price.normal;
                if (state.circuit_date_creneau_id && state.possibilite.price.creneau) {
                    price = state.possibilite.price.creneau;
                } else if (state.circuit_date_id && state.possibilite.price.date) {
                    price = state.possibilite.price.date;
                }
                subprice = stagePrice(price);
                perTours = null;
                pricePossibilite = 'oui';
                priceFrom = 'possibilite';
            }

            let total = Math.round((subprice + state.priceOptions + state.priceAutreStages) * 100) / 100;

            // Calcul de l'ancien total avec la même logique que le prix actuel.
            // #137 : on ne calcule un "ancien prix" (donc un prix barré) QUE s'il y a une vraie
            // remise (price.promo renseigné). Par défaut oldSubprice = subprice → aucun barré.
            // Sinon les forfaits tours_supplementaires (old_montant_ttc dégressif mais promo = null)
            // faisaient apparaître/disparaître le barré selon le nb de tours ("yoyo").
            let oldSubprice = subprice;
            if (!mainStage.possibilites || !mainStage.possibilites.length) {
                // Stage avec nombre de tours - calculer oldSubprice selon le nombre de tours
                if (price && price.promo && price.old_montant_ttc) {
                    if (mainNbTours <= price.nb_tour_min) {
                        oldSubprice = price.old_montant_ttc;
                    } else {
                        // Utiliser old_montant_ttc_par_tour si disponible, sinon fallback sur montant_ttc_par_tour
                        const oldPricePerTour = price.old_montant_ttc_par_tour || price.montant_ttc_par_tour;
                        oldSubprice = price.old_montant_ttc + ((mainNbTours - price.nb_tour_min) * oldPricePerTour);
                    }
                }
            } else {
                // Stage avec possibilités - utiliser oldSubprice de la possibilité
                if (state.possibilite && state.possibilite.price) {
                    let selectedPossibilitePrice = state.possibilite.price.normal;
                    if (state.circuit_date_creneau_id && state.possibilite.price.creneau) {
                        selectedPossibilitePrice = state.possibilite.price.creneau;
                    } else if (state.circuit_date_id && state.possibilite.price.date) {
                        selectedPossibilitePrice = state.possibilite.price.date;
                    }
                    if (selectedPossibilitePrice && selectedPossibilitePrice.promo && selectedPossibilitePrice.old_montant_ttc) {
                        oldSubprice = selectedPossibilitePrice.old_montant_ttc;
                    }
                }
            }

            let discountAmount = price.promo ? Math.round((oldSubprice - subprice) * 100) / 100 : 0;

            logger.log('PRICEEEEEE', price, discountAmount);

            let oldTotal = Math.round((oldSubprice + state.priceOptions + state.oldPriceAutreStages) * 100) / 100;

            // LOGS POUR DEBUG PRIX BARRÉ
            logger.log('🚨 DEBUG PRIX BARRÉ - DÉBUT');
            logger.log('🔍 Contexte stage:', {
                experience: state.experience,
                nb_tours_bapteme: state.nb_tours_bapteme,
                nb_tours: state.nb_tours,
                mainStage: mainStage ? {stage_id: mainStage.stage_id, is_bapteme: mainStage.is_bapteme} : null,
                mainNbTours: mainNbTours
            });

            logger.log('🔍 Variable price utilisée pour oldTotal:', {
                price_exists: !!price,
                old_montant_ttc: price ? price.old_montant_ttc : 'N/A',
                montant_ttc: price ? price.montant_ttc : 'N/A',
                promo_type: price ? price.promo_type : 'N/A'
            });

            logger.log('🔍 Calcul oldTotal détail:', {
                priceOptions: state.priceOptions,
                priceAutreStages: state.priceAutreStages,
                old_montant_ttc_contribution: price ? price.old_montant_ttc : 0,
                oldTotal_calculated: oldTotal,
                total_actual: total,
                should_show_old_price: oldTotal && oldTotal > total
            });

            // Vérifier si on a des possibilités
            if (mainStage.possibilites && mainStage.possibilites.length) {
                logger.log('🔍 Stage avec possibilités:', {
                    possibilites_length: mainStage.possibilites.length,
                    possibilite_selected: state.possibilite ? {
                        id: state.possibilite.id,
                        price_normal: state.possibilite.price ? state.possibilite.price.normal : null,
                        price_creneau: state.possibilite.price ? state.possibilite.price.creneau : null,
                        price_date: state.possibilite.price ? state.possibilite.price.date : null
                    } : 'AUCUNE POSSIBILITÉ SÉLECTIONNÉE'
                });
            }

            // Vérifier si on a une sélection date/créneau
            if (state.circuit_date_creneau_id || state.circuit_date_id) {
                logger.log('🔍 Sélection date/créneau:', {
                    circuit_date_id: state.circuit_date_id,
                    circuit_date_creneau_id: state.circuit_date_creneau_id,
                    has_stage_dates: !!(mainStage.dates),
                    date_data: state.circuit_date_id && mainStage.dates ? mainStage.dates[state.circuit_date_id] : null
                });
            }

            if (state.debug_price) {
                logger.table({
                    montant_ttc_par_tour: price ? price.montant_ttc_par_tour : 'N/A',
                    old_montant_ttc: price ? price.old_montant_ttc : 'N/A',
                    nb_tours: state.nb_tours,
                    priceOptions: state.priceOptions,
                    priceAutreStages: state.priceAutreStages,
                    total: total,
                    subprice: subprice,
                    perTours: perTours,
                    pricePossibilite: pricePossibilite,
                    typePrice: typePrice,
                    priceFrom: priceFrom,
                    oldTotal: oldTotal,
                    show_old_price: oldTotal && oldTotal > total
                })
            }

            logger.log('🚨 DEBUG PRIX BARRÉ - FIN');

            logger.log(oldTotal - total);

            return {
                'oldTotal': oldTotal,
                'total': total,
                'perTours': perTours,
            }
        },
        stageValidToCart: (state) => {
            if (state.experience === 'bapteme') {
                return state.nb_tours_bapteme || state.possibilite;
            }
            return state.nb_tours || state.possibilite;
        },

        // Validation complète pour empêcher la validation du configurateur
        canValidateConfiguration: (state) => {
            if (state.isLoadingStep) return false;
            // 1. Vérification voiture obligatoire
            if (!state.voiture_id) {
                return false;
            }

            // 2. Vérification circuit obligatoire  
            if (!state.circuit_id) {
                return false;
            }

            // 3. Vérification stage obligatoire selon l'expérience
            if (state.experience === 'bapteme') {
                if (!state.stageBapteme || !state.stageBapteme.stage_id) {
                    return false;
                }
            } else {
                if (!state.stage || !state.stage.stage_id) {
                    return false;
                }
            }

            // 4. Vérification tours/possibilité obligatoire selon l'expérience
            if (state.experience === 'bapteme') {
                // En mode baptême : soit nb_tours_bapteme > 0 OU possibilité définie
                const hasValidTours = state.nb_tours_bapteme > 0;
                const hasValidPossibilite = state.possibilite && state.possibilite.id;
                if (!hasValidTours && !hasValidPossibilite) {
                    return false;
                }
            } else {
                // En mode pilotage : soit nb_tours > 0 OU possibilité définie
                const hasValidTours = state.nb_tours > 0;
                const hasValidPossibilite = state.possibilite && state.possibilite.id;
                if (!hasValidTours && !hasValidPossibilite) {
                    return false;
                }
            }

            // 5. Validation dates/créneaux
            if (state.dateMode === 'cadeau') {
                // Mode cadeau : pas de contrainte de date
                return true;
            }

            if (state.dateMode === 'choice') {
                return Boolean(state.circuit_date_id && state.circuit_date_creneau_id && state.circuit_date_creneau_voiture_id);
            }

            // Tous les critères sont satisfaits
            return true;
        },

        // Message récapitulatif de ce qui manque pour valider
        validationErrorMessage: (state) => {
            const errors = [];

            // 1. Vérification voiture obligatoire
            if (!state.voiture_id) {
                errors.push("Sélectionnez une voiture");
            }

            // 2. Vérification circuit obligatoire  
            if (!state.circuit_id) {
                errors.push("Sélectionnez un circuit");
            }

            // 3. Vérification stage obligatoire selon l'expérience
            if (state.experience === 'bapteme') {
                if (!state.stageBapteme || !state.stageBapteme.stage_id) {
                    errors.push("Choisissez une voiture et un circuit");
                }
            } else {
                if (!state.stage || !state.stage.stage_id) {
                    errors.push("Choisissez une voiture et un circuit");
                }
            }

            // 4. Vérification tours/possibilité obligatoire selon l'expérience
            if (state.experience === 'bapteme') {
                // En mode baptême : soit nb_tours_bapteme > 0 OU possibilité définie
                const hasValidTours = state.nb_tours_bapteme > 0;
                const hasValidPossibilite = state.possibilite && state.possibilite.id;
                if (!hasValidTours && !hasValidPossibilite) {
                    errors.push("Choisissez le nombre de tours ou une formule");
                }
            } else {
                // En mode pilotage : soit nb_tours > 0 OU possibilité définie
                const hasValidTours = state.nb_tours > 0;
                const hasValidPossibilite = state.possibilite && state.possibilite.id;
                if (!hasValidTours && !hasValidPossibilite) {
                    errors.push("Choisissez le nombre de tours ou une formule");
                }
            }

            // 5. Validation dates/créneaux
            if (state.isLoadingStep) {
                errors.push("Vérification des disponibilités en cours");
            } else if (state.dateMode === 'choice' && !state.circuit_date_id) {
                errors.push("Sélectionnez une date pour cette prestation");
            } else if (state.dateMode === 'choice' && !state.circuit_date_creneau_id) {
                errors.push("Sélectionnez un créneau horaire pour la date choisie");
            }

            return errors.join(", ");
        },

        // on récupére les circuis des stages en unique
        circuitsStages: (state) => {
            if (!state.stages.length) {
                return [];
            }
            let circuits = {};
            state.stages.forEach(stage => {
                stage.circuits.forEach(circuit => {
                    circuits[circuit.id] = circuit;
                })
            });
            return Object.values(circuits);
        },
        // on filtre via la voiture les circuits ou on retourne tout si pas de voiture_id
        circuitsStagesVoiture: (state) => {
            if (!state.circuitsStages.length) {
                return [];
            }
            if (!state.voiture_id) {
                return state.circuitsStages;
            }
            return state.circuitsStages.filter(circuit => circuit.voiture_id == state.voiture_id);
        },

        // Filtrer les voitures selon l'expérience
        // Why: après une re-fetch avec voiture_id, state.stages ne contient plus que la voiture
        // cliquée, donc filtrer via stages faisait disparaître toutes les autres voitures.
        // Le backend (GlobalStep::transformVoitures) n'inclut déjà que les voitures ayant un prix
        // compatible avec le mode, donc filtrer sur voiture.prices est suffisant et stable.
        voituresFiltered: (state) => {
            if (!state.voitures.length) {
                return [];
            }

            if (state.experience === 'bapteme') {
                return state.voitures.filter(voiture => {
                    if (!voiture.prices) return false;
                    const prices = Array.isArray(voiture.prices) ? voiture.prices : Object.values(voiture.prices);
                    return prices.some(price => price && (price.is_bapteme === true || price.is_bapteme == 1));
                });
            }

            return state.voitures;
        },

        // Filtrer les circuits selon l'expérience
        circuitsFiltered: (state) => {
            if (!state.circuits.length) {
                return [];
            }

            // Si mode baptême, ne garder que les circuits avec is_bapteme = true
            /*if (state.experience === 'bapteme') {
                logger.log('🔍 Filtrage circuits - Mode baptême, filtrage sur is_bapteme = true');
                const filtered = state.circuits.filter(circuit => {
                    logger.log('🔍 Vérification circuit:', circuit.id, 'is_bapteme:', circuit.is_bapteme, 'prices:', circuit.prices);
                    // Vérifier s'il y a une price avec is_bapteme = true ou directement is_bapteme
                    const hasBaptemePrice = circuit.prices && circuit.prices.some(price => price.is_bapteme === true || price.is_bapteme == 1);
                    const directBapteme = circuit.is_bapteme === true || circuit.is_bapteme == 1;
                    return hasBaptemePrice || directBapteme;
                });
                logger.log('🔍 Circuits filtrés pour baptême:', filtered.length, 'sur', state.circuits.length);
                return filtered;
            }*/

            // Mode pilotage : tous les circuits
            logger.log('🔍 Filtrage circuits - Mode pilotage, tous les circuits');
            return state.circuits;
        },
        // Icône dynamique pour l'étape voiture
        voitureStepIcon: (state) => {
            if (!state.voiture_id) return 'bi bi-car-front';
            // Ici on peut ajouter une logique basée sur le type de voiture ou la marque
            return 'bi bi-car-front-fill';
        },
        // Icône dynamique pour l'étape circuit
        circuitStepIcon: (state) => {
            if (!state.circuit_id) return 'bi bi-map';
            // Ici on peut ajouter une logique basée sur le type de circuit
            return 'bi bi-geo-alt-fill';
        },
        // Icône dynamique pour l'étape tour basée sur le nombre de tours
        tourStepIcon: (state) => {
            if (state.nb_tours === 0) return 'bi bi-speedometer';
            if (state.nb_tours <= 3) return 'bi bi-speedometer';
            if (state.nb_tours <= 6) return 'bi bi-speedometer2';
            return 'bi bi-lightning-fill';
        },
        // Icône dynamique pour l'étape créneau basée sur le type
        creneauStepIcon: (state) => {
            if (state.dateMode === 'cadeau') return 'bi bi-gift';
            if (state.date) return 'bi bi-calendar-check-fill';
            return 'bi bi-calendar-check';
        },
        // Icône dynamique pour l'étape option basée sur le nombre d'options
        optionStepIcon: (state) => {
            const nbOptions = state.options.length;
            if (nbOptions === 0) return 'bi bi-plus-circle';
            if (nbOptions === 1) return 'bi bi-check-circle';
            return 'bi bi-check-circle-fill';
        },
        // Icône dynamique pour l'étape complément basée sur le nombre de compléments
        complementStepIcon: (state) => {
            const nbComplement = state.stage_ids.length;
            if (nbComplement === 0) return 'bi bi-person-plus';
            if (nbComplement === 1) return 'bi bi-people';
            return 'bi bi-people-fill';
        },
        // Icônes des étapes avec sélections (exclut l'étape 1 = voiture)
        completedStepsIcons: (state) => {
            const icons = [];

            // On parcourt toutes les étapes sauf la première (voiture)
            const stepsWithoutFirst = state.steps.slice(1);

            stepsWithoutFirst.forEach(step => {
                let hasSelection = false;
                let icon = '';

                switch (step) {
                    case 'circuit':
                        hasSelection = !!state.circuit_id;
                        icon = state.circuitStepIcon;
                        break;
                    case 'tour':
                        hasSelection = state.nb_tours > 0;
                        icon = state.tourStepIcon;
                        break;
                    case 'creneau':
                        hasSelection = !!state.circuit_date_creneau_id || state.dateMode === 'cadeau';
                        icon = state.creneauStepIcon;
                        break;
                    case 'option':
                        hasSelection = state.options.length > 0;
                        icon = state.optionStepIcon;
                        break;
                    case 'complement':
                        hasSelection = state.stage_ids.length > 0;
                        icon = state.complementStepIcon;
                        break;
                }

                if (hasSelection && icon) {
                    icons.push({
                        step: step,
                        icon: icon
                    });
                }
            });

            return icons;
        },
    },

    actions: {
        // Actions de navigation
        setStep(value) {
            this.step = value;
        },
        getStepNumber(step) {
            return this.steps.indexOf(step) + 1;
        },
        isCompleted(step) {
            let index = this.steps.indexOf(step)
            let indexCurrent = this.steps.indexOf(this.step)
            return index < indexCurrent
        },
        setFrom(value) {
            this.from = value;
        },

        // Actions pour le type d'expérience
        setExperience(value) {
            logger.log('🔧 setExperience appelé avec:', value);
            logger.log('🔧 Ancienne valeur experience:', this.experience);
            this.experience = value;
            logger.log('🔧 Nouvelle valeur experience:', this.experience);
        },

        // Actions pour les identifiants principaux
        setVoitureId(value) {
            this.voiture_id = parseInt(value);
        },
        setCircuitId(value) {
            this.circuit_id = parseInt(value);
        },
        setStageId(value) {
            this.stage_id = parseInt(value);
        },

        // Actions pour les tours
        setNbTours(value) {
            this.nb_tours = parseInt(value);
        },
        incrementNbTours() {
            this.nb_tours++;
        },
        decrementNbTours() {
            this.nb_tours--;
        },
        clampBaptemeTours() {
            const stage = this.stageBapteme;
            if (!stage.stage_id || stage.is_autre) return;
            const minimum = Number(stage.nb_tour_min) || 1;
            const maximum = Number(stage.nb_tour_max) || minimum;
            if (this.experience === 'bapteme' || this.nb_tours_bapteme > 0) {
                this.nb_tours_bapteme = Math.min(maximum, Math.max(minimum, Number(this.nb_tours_bapteme) || minimum));
            }
            if (this.stageBaptemeInStageIds) {
                this.stageBaptemeInStageIds.nb_tour = this.nb_tours_bapteme;
            }
        },
        incrementNbToursBapteme() {
            this.clampBaptemeTours();
            if (this.nb_tours_bapteme >= Number(this.stageBapteme.nb_tour_max)) return;
            this.nb_tours_bapteme++;

            // En mode baptême, ne pas ajouter le baptême dans stage_ids car il sera le stage principal
            if (this.experience === 'bapteme') {
                // Mode baptême : ne rien faire avec stage_ids
                return;
            }

            // Mode pilotage + baptême : ajouter le baptême en complément
            if (!this.stageBaptemeInStageIds && this.stageBapteme) {
                this.stage_ids.push(this.stageBapteme.form)
            }
            if (this.stageBaptemeInStageIds) {
                this.stageBaptemeInStageIds.nb_tour = this.nb_tours_bapteme;
            }
        },
        decrementNbToursBapteme() {
            this.nb_tours_bapteme--;

            // En mode baptême, ne pas toucher stage_ids
            if (this.experience === 'bapteme') {
                return;
            }

            // Mode pilotage + baptême : gérer la suppression du complément
            if (this.nb_tours_bapteme < 1 && this.stageBaptemeInStageIds) {
                this.stage_ids = this.stage_ids.filter(v => v.id != this.stageBaptemeInStageIds.id)
            }
            if (this.stageBaptemeInStageIds) {
                this.stageBaptemeInStageIds.nb_tour = this.nb_tours_bapteme;
            }
        },
        setPossibilite(value) {
            this.possibilite = value;

            if (value?.type === 'nb_tour') {
                if (this.experience === 'bapteme') {
                    this.nb_tours_bapteme = parseInt(value.valeur);
                } else {
                    this.nb_tours = parseInt(value.valeur);
                }
            }
        },

        // Actions pour les options
        addOption(reference, qty) {
            let index = this.options.findIndex(v => v.option_id == reference.option_id);
            if (qty === 0 && index !== -1) {
                this.removeOption(reference);
                return
            }
            if (qty === 0 && index === -1) {
                return
            }

            if (index === -1) {
                this.options.push({
                    quantity: qty,
                    option_id: reference.option_id,
                    product_id: reference.product_id,
                    reference_id: reference.id,
                    option_reference_id: reference.id,
                    code: reference.code,
                    montant_ttc: reference.montant_ttc,
                })
            } else {
                this.options[index].quantity = qty;
            }
        },
        removeOption(reference) {
            let index = this.options.findIndex(v => v.option_id == reference.option_id);
            if (index !== -1) {
                this.options.splice(index, 1);
            }
        },

        // Actions pour les dates et créneaux
        setDateMode(value) {
            this.dateMode = value;
        },
        setCurentMonth(value) {
            this.currentMonth = value;
        },
        setCurentDate(value) {
            this.currentDate = value;
        },
        setStepDate(value) {
            this.stepDate = value;
        },
        setPreselectedDateIso(value) {
            this.preselectedDateIso = value;
        },
        setCircuitDateId(value) {
            this.circuit_date_id = parseInt(value);
        },

        // Actions pour les collections de données
        setVoitures(value) {
            this.voitures = value;
        },
        setCircuits(value) {
            this.circuits = value;
        },
        setStages(value) {
            this.stages = value;
        },
        setCircuitsVoitures(value) {
            this.circuitsVoitures = value;
        },
        setKtmStage(value) {
            this.ktmStage = value;
        },
        syncStageIdsWithAutreStages() {
            this.stage_ids = this.stage_ids
                .map(selectedForm => {
                    // Chercher la voiture dans les nouveaux autreStages
                    const matchingStage = this.autreStages.find(stage =>
                        stage.form.voiture_id === selectedForm.voiture_id
                    );

                    return matchingStage ? matchingStage.form : null;
                })
                .filter(form => form !== null); // Supprimer les voitures non trouvées
        },

        // Actions pour l'UI
        setModal(value) {
            this.modal = value;
        },
        closeModal() {
            if (this.modal) {
                this.modal.hide();
            }
        },
        async openModal(options = {}) {
            this.modalLoader = true;
            let params = {}
            if (this.step === 'voiture') {
                params.voiture_id = this.voiture_id;
                if (this.circuit_id) {
                    params.circuit_id = this.circuit_id;
                }
            } else if (this.step === 'circuit') {
                params.circuit_id = this.circuit_id;
                // Include voiture_id when starting from circuit to ensure correct preselection
                if (this.voiture_id) {
                    params.voiture_id = this.voiture_id;
                }
            } else if (this.step === 'tour') {
                params.voiture_id = this.voiture_id;
                params.circuit_id = this.circuit_id;
            }
            await this.fetchStep(this.step, params, options);
            this.modalLoader = false;
            this.modal.show();
        },
        setLoaderPlanningFront(value) {
            this.loaderPlanningFront = value;
        },
        setPrevGalleryMode(value) {
            this.prevGalleryMode = value;
        },

        // Actions pour les requêtes API
        setIsFetch(step, value) {
            this.is_fetch[step] = value;
        },
        // Helper method pour déterminer si un circuit est réellement sélectionné par l'utilisateur
        // versus seulement défini automatiquement par les relations voiture<->circuit
        isCircuitActuallySelected() {
            // Si on n'a pas de circuit_id, alors pas de sélection
            if (!this.circuit_id) {
                return false;
            }

            // Si on est dans l'étape circuit ou après, considérer comme sélection réelle
            if (this.step && this.steps.indexOf(this.step) >= this.steps.indexOf('circuit')) {
                return true;
            }

            // Si le premier step est 'circuit' et qu'on a un circuit_id, c'est une sélection réelle
            if (this.first_step === 'circuit') {
                return true;
            }

            // Sinon, probablement une relation automatique voiture->circuit
            return false;
        },
        async fetchStep(step, params, options = {}) {
            const requestVersion = ++this.stepRequestVersion;
            const selection = `${this.circuit_id}:${this.voiture_id}:${this.experience}`;
            params = {...params};
            this.isLoadingStep = true;
            params.initialStep = this.first_step;

            // Ajouter le paramètre is_bapteme si l'expérience est baptême
            if (this.experience === 'bapteme') {
                params.is_bapteme = 1;
            } else {
                params.is_bapteme = 0;
            }

            // Solution au bug : ne passer circuit_id que si un circuit est réellement sélectionné
            // Ceci évite le filtrage simultané voiture_id AND circuit_id qui bloque les stages 
            // dont les dates disponibles sont sur un autre circuit
            if (params.circuit_id && !options.circuitSelectedByUser && !this.isCircuitActuallySelected()) {
                logger.log('🔧 fetchStep: Suppression de circuit_id car pas de sélection réelle');
                delete params.circuit_id;
            }
            logger.log('🔧 fetchStep: Paramètres de la requête:', params);

            try {
                let response = await axios.get('/api/configurator/global', {params});
                if (requestVersion !== this.stepRequestVersion || selection !== `${this.circuit_id}:${this.voiture_id}:${this.experience}`) return;
                if (response.status === 200 && response.data && response.data.datas) {
                    const datas = response.data.datas;

                    if (datas.configurateur_images) {
                        this.configurateurImages = datas.configurateur_images;
                    }

                    if (datas.voitures) {
                        if (this.steps[0] == 'circuit' || this.voitures.length == 0) {
                            this.voitures = datas.voitures;
                        } else {
                            let newVoitures = [...this.voitures];
                            datas.voitures.forEach(voiture => {
                                if (!newVoitures.find(v => v.id == voiture.id)) {
                                    newVoitures.push(voiture);
                                }
                            })
                            newVoitures = newVoitures.map(voiture => {
                                let found = datas.voitures.find(v => v.id == voiture.id);
                                if (found) {
                                    return found;
                                }
                                return voiture;
                            })
                            newVoitures = newVoitures.sort((a, b) => {
                                return a.display_order - b.display_order;
                            })
                            this.voitures = newVoitures;
                        }
                    }

                    if (datas.circuits) {
                        if (this.steps[0] == 'voiture' || this.circuits.length == 0) {
                            this.circuits = datas.circuits;
                        } else {
                            let newCircuits = [...this.circuits];
                            datas.circuits.forEach(circuit => {
                                if (!newCircuits.find(v => v.id == circuit.id)) {
                                    newCircuits.push(circuit);
                                }
                            })
                            newCircuits = newCircuits.map(circuit => {
                                let found = datas.circuits.find(v => v.id == circuit.id);
                                if (found) {
                                    return found;
                                }
                                return circuit;
                            })
                            newCircuits = newCircuits.sort((a, b) => {
                                return a.display_order - b.display_order;
                            })
                            this.circuits = newCircuits;
                        }

                        // Vérifier si le circuit_id actuel est valide dans la liste des circuits
                        const circuitValide = this.circuit_id && this.circuits.find(c => c.id == this.circuit_id);

                        // Prendre le 1er circuit par défaut si pas de circuit_id OU si le circuit actuel n'est pas dans la liste
                        let circuitChanged = false;
                        if ((!this.circuit_id || !circuitValide) && this.circuits.length > 0) {
                            this.circuit_id = this.circuits[0].id;
                            circuitChanged = true;
                        }

                        // Si le circuit a été changé automatiquement, re-fetch les données pour synchroniser les stages
                        if (circuitChanged) {
                            await this.fetchStep(step, {
                                ...params,
                                voiture_id: this.voiture_id,
                                circuit_id: this.circuit_id,
                                initialStep: this.first_step,
                                is_bapteme: this.experience === 'bapteme' ? 1 : 0
                            }, {circuitSelectedByUser: true});
                            return;
                        }
                    }

                    if (datas.stages) {
                        this.stages = datas.stages;
                        const selectedStage = this.experience === 'bapteme' ? this.stageBapteme : this.stage;

                        // Valider la disponibilité de la date/créneau après chargement des nouvelles données
                        this.validateDateCreneauAvailability();

                        // Recherche des circuit_date_id correspondant à selectedDateIso (preselectedDateIso)
                        if (this.preselectedDateIso) {
                            const preselectedDateIso = this.preselectedDateIso;
                            this.preselectedDateIso = null;
                            let monthSelected, dateSelected, circuit_date_id_found;

                            (selectedStage.months || []).forEach(month => {
                                month.dates.forEach(date => {
                                    logger.log('🔍 Comparaison dates:', {
                                        original: date.date,
                                        preselectedDateIso: this.preselectedDateIso,
                                        date_human: date.date_human
                                    });

                                    // Comparer directement les dates sans transformation
                                    if (date.date === preselectedDateIso) {
                                        monthSelected = month;
                                        dateSelected = date;
                                        circuit_date_id_found = date.circuit_date_id;
                                    }
                                })
                            })

                            if (monthSelected && dateSelected && circuit_date_id_found) {
                                this.clearCreneauSelection();
                                this.currentMonth = monthSelected;
                                this.currentDate = dateSelected;
                                this.circuit_date_id = circuit_date_id_found;
                                this.stepDate = 'creneau';
                                // Définir le mode de date sur 'choice' pour afficher la sélection de date
                                this.dateMode = 'choice';
                                // Réinitialiser preselectedDateIso après utilisation
                                this.preselectedDateIso = null;
                                logger.log('🔍 Date pré-sélectionnée traitée:', {
                                    circuit_date_id: this.circuit_date_id,
                                    currentDate: this.currentDate,
                                    dateMode: this.dateMode,
                                    stepDate: this.stepDate
                                });
                            }
                        }
                        // Logique existante si circuit_date_id est déjà défini
                        else if (this.circuit_date_id && this.stage_id && selectedStage) {
                            let monthSelected, dateSelected;
                            (selectedStage.months || []).forEach(month => {
                                month.dates.forEach(date => {
                                    if (date.circuit_date_id == this.circuit_date_id) {
                                        monthSelected = month;
                                        dateSelected = date;
                                    }
                                })
                            })
                            if (monthSelected && dateSelected) {
                                this.currentMonth = monthSelected;
                                this.currentDate = dateSelected;
                                this.stepDate = 'creneau';
                            }
                        }
                    }


                    if (datas.autreStages) {
                        this.autreStages = datas.autreStages;
                        // Synchroniser les voitures sélectionnées à l'étape 6 avec les nouveaux autreStages
                        this.syncStageIdsWithAutreStages();
                    }

                    if (datas.ktmStages && datas.ktmStages.length > 0) {
                        this.ktmStage = datas.ktmStages[0];
                    } else {
                        this.ktmStage = null;
                    }

                    if (!this.voiture_id && !this.circuit_id && this.voitures.length) {
                        this.voiture_id = this.voitures[0].id;
                    }

                    // Logique pour les relations entre voitures et circuits
                    if (datas.voitures && this.voiture_id && !this.circuit_id && this.voiture && this.voiture.price && this.voiture.price.circuit_id) {
                        this.circuit_id = this.voiture.price.circuit_id;
                    }
                    if (datas.circuits && this.circuit_id && !this.voiture_id && this.circuit && this.circuit.price && this.circuit.price.voiture_id) {
                        this.voiture_id = this.circuit.price.voiture_id;
                    }

                    // Logique pour les tours
                    if (this.experience === 'bapteme') {
                        // Mode baptême : initialiser nb_tours_bapteme avec le minimum du stage baptême
                        if (!this.nb_tours_bapteme && this.stageBapteme.stage_id) {
                            if (this.stageBapteme.tours_conseilles && this.stageBapteme.tours_conseilles.length) {
                                this.nb_tours_bapteme = parseInt(this.stageBapteme.tours_conseilles[0].nb_tours);
                            } else {
                                this.nb_tours_bapteme = this.stageBapteme.nb_tour_min;
                            }
                        }
                    } else {
                        // Mode pilotage : logique existante pour nb_tours
                        if (!this.nb_tours && this.stage.stage_id) {
                            if (this.stage.tours_conseilles.length) {
                                this.nb_tours = parseInt(this.stage.tours_conseilles[0].nb_tours);
                            } else {
                                this.nb_tours = this.stage.nb_tour_min;
                            }
                        }
                    }

                    this.clampBaptemeTours();

                    // Logique pour les possibilités
                    if (this.possibilite && this.stage && this.stage.possibilites) {
                        const currentPossibilite = this.stage.possibilites.find(v => v.id == this.possibilite.id);

                        if (!currentPossibilite) {
                            this.possibilite = null
                        } else {
                            this.setPossibilite(currentPossibilite)
                        }
                    }

                    if (!this.possibilite && this.stage && this.stage.possibilites) {
                        this.setPossibilite(this.stage.possibilites[0])
                    }

                } else {
                    logger.error("fetchStep: Réponse invalide ou statut non OK", response);
                }
            } catch (error) {
                logger.error("Erreur dans fetchStep pour", step, params, error);
            } finally {
                if (requestVersion === this.stepRequestVersion) {
                    this.isLoadingStep = false;
                    this.loaderPlanningFront = false;
                }
            }
        },
        cleanStageIds() {
            this.stage_ids = this.stage_ids.filter(v => {
                return this.autreStagesFiltered.find(stage => stage.stage_id == v.id)
            });
        },

        // Toggle car experience mode for dual-mode selection in step 6
        toggleCarExperienceMode(voitureId, newMode) {
            logger.log('🔧 toggleCarExperienceMode appelé:', {voitureId, newMode});
            logger.log('🔧 Available autreStages:', this.autreStages.map(s => ({
                voiture_id: s.voiture_id,
                form_voiture_id: s.form?.voiture_id,
                voiture_title: s.voiture_title,
                dual_mode: s.dual_mode
            })));

            // Find the stage data for this specific voiture_id in autreStages
            // Check both stage.voiture_id and stage.form.voiture_id
            const stageIndex = this.autreStages.findIndex(stage =>
                stage.voiture_id === voitureId || stage.form?.voiture_id === voitureId
            );

            if (stageIndex === -1) {
                logger.warn('🚨 Stage data not found for voiture:', voitureId);
                logger.warn('🚨 Available voiture_ids:', this.autreStages.map(s => ({
                    voiture_id: s.voiture_id,
                    form_voiture_id: s.form?.voiture_id
                })));
                return;
            }

            const stageData = this.autreStages[stageIndex];
            logger.log('🔧 Found stage data:', {
                stageIndex,
                voiture_id: stageData.voiture_id,
                form_voiture_id: stageData.form?.voiture_id,
                dual_mode: stageData.dual_mode,
                available_modes: stageData.available_modes
            });

            if (!stageData.dual_mode) {
                logger.warn('🚨 Stage is not dual-mode for voiture:', voitureId);
                return;
            }

            // Check if the requested mode is available
            if (!stageData.available_modes || !stageData.available_modes.includes(newMode)) {
                logger.warn('🚨 Mode not available for this car:', newMode, 'Available:', stageData.available_modes);
                return;
            }

            // Update the stage data to use the new mode
            const newForm = stageData.forms[newMode];
            if (newForm) {
                // Create a deep copy to avoid reference issues
                const updatedForm = {...newForm};

                // Update the stage form directly in autreStages using the index
                this.autreStages[stageIndex].form = updatedForm;

                // If this car is already selected in stage_ids, update its form data
                const existingIndex = this.stage_ids.findIndex(form =>
                    form.voiture_id === voitureId
                );

                if (existingIndex !== -1) {
                    this.stage_ids[existingIndex] = {...updatedForm};
                    logger.log('🔧 Updated existing selection with new mode:', newMode);
                }

                logger.log('🔧 Car mode toggled successfully:', {
                    voitureId,
                    newMode,
                    stageIndex,
                    updatedForm
                });
            } else {
                logger.error('🚨 Form data not found for mode:', newMode, 'Available forms:', Object.keys(stageData.forms || {}));
            }
        },


        // Valider la disponibilité de la date/créneau après changement voiture/circuit
        validateDateCreneauAvailability() {
            // Ne valider que si on a une date sélectionnée
            if (!this.circuit_date_id) {
                return;
            }

            logger.log('🔧 Validation disponibilité date/créneau:', {
                circuit_date_id: this.circuit_date_id,
                circuit_date_creneau_id: this.circuit_date_creneau_id,
                voiture_id: this.voiture_id,
                circuit_id: this.circuit_id
            });

            // En mode baptême, utiliser stageBapteme ; sinon utiliser stage
            const targetStage = (this.experience === 'bapteme' && this.nb_tours_bapteme > 0)
                ? this.stageBapteme
                : this.stage;

            if (!targetStage || !targetStage.dates) {
                logger.log('🔧 Pas de stage ou dates disponibles, désélection de la date');
                this.clearDateSelection();
                return;
            }

            // Vérifier si la date existe encore
            const dateExists = targetStage.dates[this.circuit_date_id];
            if (!dateExists) {
                logger.log('🔧 Date non disponible pour le nouveau couple voiture/circuit, désélection');
                this.clearDateSelection();
                return;
            }

            // Si on a un créneau sélectionné, vérifier s'il existe encore
            if (this.circuit_date_creneau_id) {
                const creneauExists = (dateExists.creneaux || []).find(
                    creneau => creneau.circuit_date_creneau_id == this.circuit_date_creneau_id
                );

                if (!creneauExists?.circuit_date_creneau_voiture_id) {
                    logger.log('🔧 Créneau non disponible pour le nouveau couple voiture/circuit, désélection du créneau');
                    this.clearCreneauSelection();
                } else {
                    this.circuit_date_creneau_voiture_id = creneauExists.circuit_date_creneau_voiture_id;
                }
            }

            logger.log('🔧 Date/créneau valides pour le nouveau couple voiture/circuit');
        },

        // Désélectionner complètement date et créneau
        clearDateSelection() {
            logger.log('🔧 Désélection complète de la date et créneau');
            this.circuit_date_id = null;
            this.circuit_date_creneau_id = null;
            this.circuit_date_creneau_voiture_id = null;
            this.currentDate = null;
            this.currentMonth = null;
            this.stepDate = 'month';
            this.creneau_human = null;
            this.date = null;
            this.creneau = null;
            this.currentCreneau = null;
            // Keep the user's dated-booking choice: require a new date.
        },

        // Désélectionner seulement le créneau (garder la date)
        clearCreneauSelection() {
            logger.log('🔧 Désélection du créneau seulement');
            this.circuit_date_creneau_id = null;
            this.circuit_date_creneau_voiture_id = null;
            this.creneau_human = null;
            this.creneau = null;
            this.stepDate = 'creneau'; // Rester sur la sélection de créneau
        },

        // Actions de réinitialisation
        reset() {
            ++this.stepRequestVersion;
            this.stage_id = null;
            this.voiture_id = null;
            this.circuit_id = null;
            this.nb_tours = 0;
            this.loaderPlanningFront = false;
            this.modalLoader = false;
            this.isLoadingStep = false;
            this.is_fetch.tour = false;
            this.step = 'voiture';
        },
        cleanConfigurateur() {
            // Close the modal before resetting the state
            if (this.modal) {
                this.closeModal();
            }

            // Reset all state values to default
            let defaultValue = {
                is_fetch: {
                    tour: false,
                },
                stage_id: null,
                stage_ids: [],
                voiture_id: null,
                circuit_id: null,
                nb_tours: 0,
                nb_tours_bapteme: 0,
                possibilite: null,
                options: [],
                stages: [],
                circuits: [],
                voitures: [],
                circuitsVoitures: [],
                autreStages: [],
                ktmStage: null,
                step: null,
                circuit_date_creneau_id: null,
                circuit_date_creneau_voiture_id: null,
                circuit_date_id: null,
                creneau_human: null,
                dateMode: 'cadeau',
                date: null,
                creneau: null,
                currentMonth: null,
                currentDate: null,
                stepDate: 'month',
                preselectedDateIso: null,
                modalLoader: false,
                isLoadingStep: false,
                loaderPlanningFront: false,
                prevGalleryMode: null,
            };

            for (let key in defaultValue) {
                this[key] = defaultValue[key];
            }

            // Explicitly clear localStorage for configurateur data
            // This ensures all persisted data is removed
            try {
                const configurateurKeys = ['stage_id', 'voiture_id', 'circuit_id', 'nb_tours'];
                configurateurKeys.forEach(key => {
                    localStorage.removeItem(`configurateur.${key}`);
                });

                // Also clear any potential full store data
                localStorage.removeItem('configurateur');

                logger.log('✅ Configurateur reset: All data cleared from localStorage');
            } catch (error) {
                logger.warn('⚠️ Error clearing localStorage:', error);
            }
        },
        applyMarketingRounding(price) {
            return applyMarketingRounding(price);
        },
    },
})
